import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Minus, Trash2, ShoppingBag, ArrowLeft, ShieldCheck, Truck } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { StockBadge } from '../components/StockBadge';
export function Cart() {
  const {
    items,
    removeFromCart,
    updateQuantity,
    subtotal,
    itemCount
  } = useCart();
  if (items.length === 0) {
    return <div className="bg-gray-50 min-h-[60vh] flex items-center" data-id="element-202">
        <div className="max-w-md mx-auto text-center px-4 py-16" data-id="element-203">
          <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-6" data-id="element-204" />
          <h1 className="text-3xl font-extrabold text-yarbo-dark mb-3" data-id="element-205">
            Your cart is empty
          </h1>
          <p className="text-gray-600 mb-8" data-id="element-206">
            Browse Yarbo systems, bundles, modules, and accessories.
          </p>
          <Link to="/shop" className="inline-block bg-yarbo-dark text-white font-bold px-8 py-4 rounded-lg hover:bg-black transition-colors" data-id="element-207">
            Shop All Products
          </Link>
        </div>
      </div>;
  }
  return <div className="bg-gray-50 min-h-screen" data-id="element-208">
      <div className="bg-white border-b border-gray-200" data-id="element-209">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-210">
          <h1 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-1" data-id="element-211">
            Your Cart
          </h1>
          <p className="text-gray-600 text-sm" data-id="element-212">
            {itemCount} item{itemCount !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-213">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-214">
          {/* Items */}
          <div className="lg:col-span-2 space-y-3" data-id="element-215">
            {items.map(({
            product,
            quantity
          }) => <motion.div key={product.id} layout className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 flex gap-4" data-id="element-216">
                <Link to={`/product/${product.slug}`} className="w-24 h-24 sm:w-28 sm:h-28 bg-gray-50 rounded-lg p-3 flex-shrink-0" data-id="element-217">
                  <img src={product.image} alt={product.name} className="w-full h-full object-contain" data-id="element-218" />
                </Link>
                <div className="flex-1 min-w-0 flex flex-col" data-id="element-219">
                  <div className="flex justify-between gap-3 mb-2" data-id="element-220">
                    <div data-id="element-221">
                      <Link to={`/product/${product.slug}`} className="font-bold text-yarbo-dark hover:text-yarbo-yellow leading-tight" data-id="element-222">
                        {product.name}
                      </Link>
                      <div className="mt-2" data-id="element-223">
                        <StockBadge status={product.status} data-id="element-224" />
                      </div>
                    </div>
                    <button onClick={() => removeFromCart(product.id)} className="text-gray-400 hover:text-red-500" aria-label="Remove" data-id="element-225">
                      <Trash2 className="w-4 h-4" data-id="element-226" />
                    </button>
                  </div>
                  <div className="flex items-end justify-between mt-auto gap-3" data-id="element-227">
                    <div className="flex items-center border border-gray-200 rounded-lg" data-id="element-228">
                      <button onClick={() => updateQuantity(product.id, quantity - 1)} className="p-2 hover:bg-gray-50" data-id="element-229">
                        <Minus className="w-3.5 h-3.5" data-id="element-230" />
                      </button>
                      <span className="px-4 font-bold text-sm" data-id="element-231">{quantity}</span>
                      <button onClick={() => updateQuantity(product.id, quantity + 1)} className="p-2 hover:bg-gray-50" data-id="element-232">
                        <Plus className="w-3.5 h-3.5" data-id="element-233" />
                      </button>
                    </div>
                    <div className="text-right" data-id="element-234">
                      <div className="text-xs text-gray-500" data-id="element-235">
                        ${product.price.toLocaleString()} each
                      </div>
                      <div className="text-lg font-extrabold text-yarbo-dark" data-id="element-236">
                        ${(product.price * quantity).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>)}

            <Link to="/shop" className="inline-flex items-center text-sm font-bold text-gray-700 hover:text-yarbo-dark pt-4" data-id="element-237">
              <ArrowLeft className="w-4 h-4 mr-2" data-id="element-238" /> Continue Shopping
            </Link>
          </div>

          {/* Summary */}
          <aside className="lg:col-span-1" data-id="element-239">
            <div className="bg-white rounded-xl border border-gray-200 p-6 sticky top-24" data-id="element-240">
              <h2 className="text-lg font-bold text-yarbo-dark mb-4" data-id="element-241">
                Order Summary
              </h2>

              <div className="space-y-3 pb-4 border-b border-gray-100 mb-4 text-sm" data-id="element-242">
                <div className="flex justify-between" data-id="element-243">
                  <span className="text-gray-600" data-id="element-244">
                    Subtotal ({itemCount} items)
                  </span>
                  <span className="font-bold text-yarbo-dark" data-id="element-245">
                    ${subtotal.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-246">
                  <span className="text-gray-600" data-id="element-247">Shipping</span>
                  <span className="font-medium text-gray-500" data-id="element-248">
                    Calculated at checkout
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-249">
                  <span className="text-gray-600" data-id="element-250">Taxes</span>
                  <span className="font-medium text-gray-500" data-id="element-251">
                    Calculated at checkout
                  </span>
                </div>
              </div>

              <div className="flex justify-between mb-6" data-id="element-252">
                <span className="text-lg font-bold text-yarbo-dark" data-id="element-253">
                  Estimated Total
                </span>
                <span className="text-2xl font-extrabold text-yarbo-dark" data-id="element-254">
                  ${subtotal.toLocaleString()}
                </span>
              </div>

              <button className="w-full bg-yarbo-dark text-white font-bold py-4 rounded-lg hover:bg-black transition-colors text-base" data-id="element-255">
                Proceed to Checkout
              </button>

              <div className="mt-5 space-y-2 text-xs text-gray-500" data-id="element-256">
                <div className="flex items-center gap-2" data-id="element-257">
                  <ShieldCheck className="w-4 h-4 text-gray-400" data-id="element-258" /> Secure
                  encrypted checkout
                </div>
                <div className="flex items-center gap-2" data-id="element-259">
                  <Truck className="w-4 h-4 text-gray-400" data-id="element-260" /> Nationwide
                  shipping
                </div>
              </div>

              <p className="text-xs text-gray-500 mt-5 pt-4 border-t border-gray-100" data-id="element-261">
                All items in stock and ready to ship. Shipping and taxes are
                calculated at checkout.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>;
}